/**
 * Per-joint replacement point score (TKA / THA), driven by a ReplacementConfig
 * (utils/jointReplacement.ts, mirroring QME - Tool's pointScoreConfig). Ticking
 * "<Side> ... replacement" pre-fills the answers from that joint's exam; the
 * joint is then rated by this score instead of its range of motion.
 */
import React from 'react';
import AuthenticatedInput from '../AuthenticatedInput';
import AuthenticatedSelect from '../AuthenticatedSelect';
import { useExamTab } from './ExamTabContext';
import { ReplacementConfig, Side } from '../../utils/jointReplacement';

const JointReplacementPanel: React.FC<{ config: ReplacementConfig }> = ({ config }) => {
  const { examData, setExamData, user, userProfile, startManualSession, setShowAuthModal } = useExamTab();
  const regionData: any = (examData as any)?.[config.region] || {};
  const gate = { user, userProfile, onSessionStart: startManualSession, onShowAuthModal: () => setShowAuthModal(true) };
  const jointWord = config.region === 'knees' ? 'knee' : 'hip';

  const patch = (p: Record<string, any>) =>
    setExamData((prev: any) => (prev ? { ...prev, [config.region]: { ...prev[config.region], ...p } } : prev));

  const toggle = (side: Side, on: boolean) =>
    patch({
      [`${config.key}_applies_${side}`]: on,
      ...(on && !regionData[`${config.key}_${side}`] ? { [`${config.key}_${side}`]: config.defaults(regionData, side) } : {}),
    });

  const setField = (side: Side, field: string, value: any) =>
    patch({ [`${config.key}_${side}`]: { ...(regionData[`${config.key}_${side}`] || config.defaults(regionData, side)), [field]: value } });

  return (
    <div className="mt-16">
      <div className="border-b border-border pb-2 mb-4">
        <h4 className="text-lg font-medium">{config.title}</h4>
        <p className="text-content-tertiary text-sm mt-1">
          {config.table}. A replaced {jointWord} is rated by this score instead of its range of motion.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {(['right', 'left'] as Side[]).map((side) => {
          const applies = !!regionData[`${config.key}_applies_${side}`];
          const answers = regionData[`${config.key}_${side}`] || config.defaults(regionData, side);
          const out = applies ? config.calc(answers, side) : null;
          const Side = side === 'right' ? 'Right' : 'Left';
          const total = out?.breakdown?.total_points ?? out?.breakdown?.total;
          return (
            <div key={side} className={`rounded-lg border p-4 ${applies ? 'border-accent-500/40' : 'border-border'}`}>
              <label className="flex items-center gap-2 font-medium cursor-pointer">
                <input type="checkbox" checked={applies} disabled={!user} onChange={(e) => toggle(side, e.target.checked)} />
                {Side} {jointWord} replacement ({config.key.toUpperCase()})
              </label>

              {applies && (
                <div className="mt-4 space-y-4 text-sm">
                  {config.sections.map((sec) => (
                    <div key={sec.title}>
                      <p className="text-xs uppercase tracking-wide text-content-tertiary mb-2">{sec.title}</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 items-center">
                        {sec.fields.map((f) => (
                          <React.Fragment key={f.field}>
                            <span className="text-content-secondary">{f.label}{f.kind === 'number' ? ` ${f.unit ?? '°'}` : ''}</span>
                            {f.kind === 'select' ? (
                              <AuthenticatedSelect {...gate} value={String(answers[f.field] ?? '')} onChange={(v) => setField(side, f.field, v)}>
                                {f.options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                              </AuthenticatedSelect>
                            ) : (
                              <AuthenticatedInput {...gate} value={answers[f.field] ?? ''} onChange={(v) => setField(side, f.field, v)} />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                  {out && (
                    <p className="font-sans" aria-live="polite">
                      <span className="text-content-secondary">{total} points, {String(out.breakdown?.result).toLowerCase()} result:</span>{' '}
                      <span className="font-semibold text-content">{out.wpi}% WPI</span>
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default JointReplacementPanel;
